/**
 * Reusable component for clickable text with copy-to-clipboard functionality
 * 
 * Provides consistent styling, hover effects, and pulse animation feedback.
 * Includes CSS animation - no need to add separately!
 */

import { type ReactNode } from "react";

// CSS animation for copy pulse effect
const COPY_PULSE_DURATION_MS = 250;

const copyPulseAnimation = `
@keyframes copy-pulse {
  0% { opacity: 1; transform: scale(1); }
  10% { opacity: 0.5; transform: scale(1); }
  20% { opacity: 1; transform: scale(0.98); }
  75% { opacity: 1; transform: scale(1); }
  100% { opacity: 1; transform: scale(1); }
}
.copy-pulse {
  animation: copy-pulse ${COPY_PULSE_DURATION_MS}ms ease-in-out;
}
`;

export interface CopyableTextProps {
  /** Unique identifier for this text (used for pulse animation tracking) */
  id: string;
  /** The text content to display */
  children: ReactNode;
  /** The text to copy (defaults to id if not provided) */
  textToCopy?: string;
  /** Callback when text is clicked */
  onCopy: (id: string, text?: string) => void;
  /** Whether this text is currently showing pulse animation */
  isPulsing?: boolean;
  /** Tooltip text (defaults to full id value) */
  title?: string;
  /** Additional CSS classes */
  className?: string;
  /** Base styling variant */
  variant?: 'default' | 'muted' | 'primary';
  /** Text size */
  size?: 'xs' | 'sm' | 'base';
  /** Whether to stop event propagation on click */
  stopPropagation?: boolean;
}

/**
 * Clickable text component with copy-to-clipboard and pulse animation
 * 
 * Automatically includes CSS animation styles - no need to add <style> tag!
 * 
 * @example
 * ```tsx
 * const { copyToClipboard, isCopied } = useCopyToClipboard();
 * 
 * <CopyableText
 *   id={user.id}
 *   onCopy={copyToClipboard}
 *   isPulsing={isCopied(user.id)}
 * >
 *   {user.id.slice(0, 8)}...
 * </CopyableText>
 * ```
 */
export function CopyableText({
  id,
  children,
  textToCopy,
  onCopy,
  isPulsing = false,
  title,
  className = "",
  variant = 'default',
  size = 'xs',
  stopPropagation = true,
}: CopyableTextProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    onCopy(id, textToCopy);
  };

  // Variant styles
  const variantClasses = {
    default: 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400',
    muted: 'text-gray-500 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400',
    primary: 'text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400',
  };

  // Size styles
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
  };

  return (
    <>
      {/* Include CSS animation once per component instance */}
      <style>{copyPulseAnimation}</style>
      
      <span
        className={`
          inline-block font-mono cursor-pointer transition-colors
          ${variantClasses[variant]}
          ${sizeClasses[size]}
          ${isPulsing ? 'copy-pulse' : ''}
          ${className}
        `.trim()}
        onClick={handleClick}
        title={title ?? id}
      >
        {children}
      </span>
    </>
  );
}

/**
 * Container component that includes CSS animation styles once for multiple CopyableText instances
 * 
 * Use this when you have multiple CopyableText components to avoid duplicate <style> tags
 * 
 * @example
 * ```tsx
 * <CopyableTextContainer>
 *   <CopyableText id="1" onCopy={copy}>ID 1</CopyableText>
 *   <CopyableText id="2" onCopy={copy}>ID 2</CopyableText>
 * </CopyableTextContainer>
 * ```
 */
export function CopyableTextContainer({ children }: { children: ReactNode }) {
  return (
    <>
      <style>{copyPulseAnimation}</style>
      {children}
    </>
  );
}

/**
 * Bare version without CSS (for use within CopyableTextContainer)
 */
export function CopyableTextBare(props: CopyableTextProps) {
  const {
    id,
    children,
    textToCopy,
    onCopy,
    isPulsing = false,
    title,
    className = "",
    variant = 'default',
    size = 'xs',
    stopPropagation = true,
  } = props;

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    onCopy(id, textToCopy);
  };

  const variantClasses = {
    default: 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400',
    muted: 'text-gray-500 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400',
    primary: 'text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400',
  };

  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
  };

  return (
    <span
      className={`
        inline-block font-mono cursor-pointer transition-colors
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${isPulsing ? 'copy-pulse' : ''}
        ${className}
      `.trim()}
      onClick={handleClick}
      title={title ?? id}
    >
      {children}
    </span>
  );
}
